import { isPlatformBrowser } from "@angular/common";
import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { ImageInterface } from "@core/interfaces/image.interface";
import { BehaviorSubject, Observable } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class ImageDataCopyService {
  private readonly _STORAGE_KEY = "astrobin_image_template";
  private readonly _templateImage$ = new BehaviorSubject<ImageInterface | null>(null);

  constructor(@Inject(PLATFORM_ID) private readonly platformId: Object) {
    this._loadFromStorage();
  }

  get templateImage$(): Observable<ImageInterface | null> {
    return this._templateImage$.asObservable();
  }

  get templateImage(): ImageInterface | null {
    return this._templateImage$.value;
  }

  setTemplateImage(image: ImageInterface): void {
    this._templateImage$.next(image);
    this._saveToStorage(image);
  }

  clearTemplate(): void {
    this._templateImage$.next(null);
    this._removeFromStorage();
  }

  extractCopyableData(templateImage: ImageInterface): Partial<ImageInterface> {
    if (!templateImage) {
      return {};
    }

    const copyableData: Partial<ImageInterface> = {};

    // Copy equipment data - extract IDs if objects, otherwise keep as is
    if (templateImage.imagingTelescopes) {
      copyableData.imagingTelescopes = templateImage.imagingTelescopes.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.imagingTelescopes2) {
      copyableData.imagingTelescopes2 = templateImage.imagingTelescopes2.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.guidingTelescopes) {
      copyableData.guidingTelescopes = templateImage.guidingTelescopes.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.guidingTelescopes2) {
      copyableData.guidingTelescopes2 = templateImage.guidingTelescopes2.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.imagingCameras) {
      copyableData.imagingCameras = templateImage.imagingCameras.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.imagingCameras2) {
      copyableData.imagingCameras2 = templateImage.imagingCameras2.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.guidingCameras) {
      copyableData.guidingCameras = templateImage.guidingCameras.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.guidingCameras2) {
      copyableData.guidingCameras2 = templateImage.guidingCameras2.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.mounts) {
      copyableData.mounts = templateImage.mounts.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.mounts2) {
      copyableData.mounts2 = templateImage.mounts2.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.filters) {
      copyableData.filters = templateImage.filters.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.filters2) {
      copyableData.filters2 = templateImage.filters2.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.accessories) {
      copyableData.accessories = templateImage.accessories.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.accessories2) {
      copyableData.accessories2 = templateImage.accessories2.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.software) {
      copyableData.software = templateImage.software.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }
    if (templateImage.software2) {
      copyableData.software2 = templateImage.software2.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }

    // Copy acquisition data
    if (templateImage.acquisitionType !== undefined) {
      copyableData.acquisitionType = templateImage.acquisitionType;
    }
    if (templateImage.subjectType !== undefined) {
      copyableData.subjectType = templateImage.subjectType;
    }
    if (templateImage.dataSource !== undefined) {
      copyableData.dataSource = templateImage.dataSource;
    }
    if (templateImage.remoteSource !== undefined) {
      copyableData.remoteSource = templateImage.remoteSource;
    }
    if (templateImage.solarSystemMainSubject !== undefined) {
      copyableData.solarSystemMainSubject = templateImage.solarSystemMainSubject;
    }

    // Copy deep sky and solar system acquisition sessions
    if (templateImage.deepSkyAcquisitions) {
      copyableData.deepSkyAcquisitions = [...templateImage.deepSkyAcquisitions];
    }
    if (templateImage.solarSystemAcquisitions) {
      copyableData.solarSystemAcquisitions = [...templateImage.solarSystemAcquisitions];
    }

    // Copy location data
    if (templateImage.locations) {
      copyableData.locations = [...templateImage.locations];
    }

    // Copy groups and collections
    if (templateImage.partOfGroupSet) {
      copyableData.partOfGroupSet = [...templateImage.partOfGroupSet];
    }
    if (templateImage.collections) {
      copyableData.collections = [...templateImage.collections];
    }

    // Copy license
    if (templateImage.license !== undefined) {
      copyableData.license = templateImage.license;
    }

    // Copy mouse hover settings
    if (templateImage.mouseHoverImage !== undefined) {
      copyableData.mouseHoverImage = templateImage.mouseHoverImage;
    }

    // Copy allow comments setting
    if (templateImage.allowComments !== undefined) {
      copyableData.allowComments = templateImage.allowComments;
    }

    // Copy description fields (but NOT the title)
    if (templateImage.description) {
      copyableData.description = templateImage.description;
    }
    if (templateImage.descriptionBbcode) {
      copyableData.descriptionBbcode = templateImage.descriptionBbcode;
    }
    if (templateImage.descriptionProsemirror) {
      copyableData.descriptionProsemirror = templateImage.descriptionProsemirror;
    }
    if (templateImage.descriptionHtml) {
      copyableData.descriptionHtml = templateImage.descriptionHtml;
    }

    // Copy collaborators - these should be user IDs
    if (templateImage.pendingCollaborators) {
      copyableData.pendingCollaborators = [...templateImage.pendingCollaborators];
    }
    if (templateImage.collaborators) {
      copyableData.collaborators = templateImage.collaborators.map(collab =>
        typeof collab === "object" && collab !== null && "id" in collab ? collab.id : collab
      ) as any;
    }

    // Copy focal reducers (not covered above)
    if (templateImage.focalReducers) {
      copyableData.focalReducers = templateImage.focalReducers.map(item =>
        typeof item === "object" && item !== null && "id" in item ? item.id : item
      ) as any;
    }

    // Copy watermark settings
    if (templateImage.watermark !== undefined) {
      copyableData.watermark = templateImage.watermark;
    }
    if (templateImage.watermarkText) {
      copyableData.watermarkText = templateImage.watermarkText;
    }
    if (templateImage.watermarkPosition !== undefined) {
      copyableData.watermarkPosition = templateImage.watermarkPosition;
    }
    if (templateImage.watermarkSize) {
      copyableData.watermarkSize = templateImage.watermarkSize;
    }
    if (templateImage.watermarkOpacity !== undefined) {
      copyableData.watermarkOpacity = templateImage.watermarkOpacity;
    }

    // Copy other settings
    if (templateImage.link !== undefined) {
      copyableData.link = templateImage.link;
    }
    if (templateImage.linkToFits !== undefined) {
      copyableData.linkToFits = templateImage.linkToFits;
    }

    // Copy sharpen thumbnails setting
    if (templateImage.sharpenThumbnails !== undefined) {
      copyableData.sharpenThumbnails = templateImage.sharpenThumbnails;
    }

    // Note: squareCropping is NOT copied as it's image-specific

    // Copy key value tags
    if (templateImage.keyValueTags) {
      copyableData.keyValueTags = templateImage.keyValueTags;
    }

    // Copy display limitation settings
    if (templateImage.fullSizeDisplayLimitation !== undefined) {
      copyableData.fullSizeDisplayLimitation = templateImage.fullSizeDisplayLimitation;
    }
    if (templateImage.downloadLimitation !== undefined) {
      copyableData.downloadLimitation = templateImage.downloadLimitation;
    }

    // Copy max zoom level
    if (templateImage.maxZoom !== undefined) {
      copyableData.maxZoom = templateImage.maxZoom;
    }

    // Copy image adjustments widget setting
    if (templateImage.allowImageAdjustmentsWidget !== undefined) {
      copyableData.allowImageAdjustmentsWidget = templateImage.allowImageAdjustmentsWidget;
    }

    // Copy the showGuidingEquipment flag (it's an ephemeral form field)
    if (templateImage.showGuidingEquipment !== undefined) {
      copyableData.showGuidingEquipment = templateImage.showGuidingEquipment;
    }

    return copyableData;
  }

  private _loadFromStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const stored = sessionStorage.getItem(this._STORAGE_KEY);
        if (stored) {
          const image = JSON.parse(stored) as ImageInterface;
          this._templateImage$.next(image);
        }
      } catch (error) {
        console.error("Failed to load template image from storage:", error);
        this._removeFromStorage();
      }
    }
  }

  private _saveToStorage(image: ImageInterface): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.setItem(this._STORAGE_KEY, JSON.stringify(image));
      } catch (error) {
        console.error("Failed to save template image to storage:", error);
      }
    }
  }

  private _removeFromStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.removeItem(this._STORAGE_KEY);
      } catch (error) {
        console.error("Failed to remove template image from storage:", error);
      }
    }
  }
}
